import { useState, useEffect, useCallback } from 'react'
import { generateVideoThumbnail, getYouTubeThumbnail, isYouTubeUrl, preloadVideoMetadata } from '../utils/videoUtils'

interface UseVideoThumbnailOptions {
  timeOffset?: number
  quality?: number
  width?: number
  height?: number
  fallbackThumbnail?: string
}

interface UseVideoThumbnailReturn {
  thumbnail: string | null
  isLoading: boolean
  error: string | null
  generateThumbnail: () => Promise<void>
  refreshThumbnail: () => Promise<void>
}

export const useVideoThumbnail = (
  videoUrl: string | null,
  options: UseVideoThumbnailOptions = {}
): UseVideoThumbnailReturn => {
  const [thumbnail, setThumbnail] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const {
    timeOffset = 2,
    quality = 0.8,
    width,
    height,
    fallbackThumbnail
  } = options

  const generateThumbnail = useCallback(async () => {
    if (!videoUrl) {
      setThumbnail(fallbackThumbnail || null)
      return
    }

    setIsLoading(true)
    setError(null)

    try {
      // Handle YouTube URLs
      if (isYouTubeUrl(videoUrl)) {
        const youtubeThumbnail = getYouTubeThumbnail(videoUrl, 'high')
        if (youtubeThumbnail) {
          setThumbnail(youtubeThumbnail)
          setIsLoading(false)
          return
        }
      }

      // Handle uploaded videos
      const videoElement = await preloadVideoMetadata(videoUrl)
      
      const thumbnailDataUrl = await generateVideoThumbnail(videoElement, {
        timeOffset,
        quality,
        width,
        height
      })

      setThumbnail(thumbnailDataUrl)
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to generate thumbnail'
      setError(errorMessage)
      setThumbnail(fallbackThumbnail || null)
    } finally {
      setIsLoading(false)
    }
  }, [videoUrl, timeOffset, quality, width, height, fallbackThumbnail])

  const refreshThumbnail = useCallback(async () => {
    await generateThumbnail()
  }, [generateThumbnail])

  // Generate thumbnail when videoUrl changes
  useEffect(() => {
    generateThumbnail()
  }, [generateThumbnail])

  return {
    thumbnail,
    isLoading,
    error,
    generateThumbnail,
    refreshThumbnail
  }
}

